// walkthrough/surface-match.js — K853: which surface is the question about?
//
// The authored guides answer "walk me through this TASK". This answers the
// far more common "where do I do X", across every surface at once, so the
// Guide stops going silent on every question but one.
//
// NO MODEL CALL, for the same two reasons matchGuide has none (spec sections
// 6 and 10): the assistant may only ever name a catalogued destination, and
// deterministic guidance has to keep working when the model is unavailable.
//
// THE SYNONYMS ARE NOT OURS, AND THAT IS THE POINT
//   packages/shared/lib/navSynonyms.js already exists. It is curated, keyed
//   by the same nav item id, covers ~50 surfaces, and was written for exactly
//   this problem - its header records the case that prompted it, someone
//   unable to find alert configuration because the menu said "Review Rules".
//   It already maps inventory to "shadow ai".
//
//   The first draft of this file carried its own hand-written synonym table.
//   That is a second list to keep in step with the first, and the product has
//   been bitten by duplicated vocabulary before. Nav search and the Guide must
//   send the same question to the same page, so they read the same table. Add
//   a word there and both improve; there is no "guide synonyms" to forget.
//
// WHAT DECIDES A MATCH
//   navSynonyms values are word BAGS, not phrases, so this scores by how many
//   of a surface's terms the question contains. One term is only enough when
//   that term is UNIQUE across every label and bag in the registry: "incident"
//   points at one page, "tools" appears in four bags and means nothing alone.
//   The registry decides what is distinctive, so adding a surface that uses a
//   word automatically stops that word matching on its own - with nobody
//   remembering to maintain a stop list.
//
// AMBIGUITY IS A STOP, NEVER THE FIRST MATCH
//   A tie returns every tied surface rather than the earliest. resolve.js
//   applies the same rule to elements for the same reason: a confident wrong
//   destination is worse than none. It also happens to be the honest answer
//   for real questions - "how do I find shadow AI" genuinely spans AI
//   Inventory, which holds the status, and AI Discovery, which holds the
//   untriaged candidates. The product draws that distinction deliberately
//   (ConsoleApp.jsx:298), so the Guide should offer both rather than pretend.
//
// ROLE SCOPE IS THE CALLER'S JOB, and it must actually do it. Pass only the
// surfaces this role may open - ConsoleApp's VISIBLE_GROUPS is already
// filtered through routeAllowed(). Matching over the whole registry would
// offer to walk someone into a page they cannot open, and disclose it exists.

import { SURFACES } from './surfaces.generated.js';
import { NAV_SYNONYMS, synonymsFor } from '@govern360/shared/lib/navSynonyms.js';

/* A trailing plural is stripped from words longer than three characters, on
   BOTH sides, so "lab devices" reaches the term "device". Without it the
   matcher misses the most ordinary thing a person types, which is the same
   class of miss as matchGuide's original contiguous-substring rule and was
   found the same way - on a real question. Over-stripping ("status" ->
   "statu") is harmless because it is symmetric: both sides normalise
   identically, so the two still meet. The >3 floor keeps "is" and "as". */
/* K854 - ONE-WAY STEMMING WAS WRONG, and the failure was invisible.
   The previous rule stripped a trailing plural and claimed safety because it
   was applied to both sides. That only holds for regular plurals:
     policies -> polici    but policy -> policy
     devices  -> devic     but device -> device
     statuses -> status    but status -> statu
   So the word "policy" could not reach the page called Policies, and the
   tests that "proved plurals worked" never once compared a plural against a
   singular - they passed for other reasons.

   Matching now compares VARIANT SETS instead of canonical forms: two words
   match when their expansions intersect. That is symmetric by construction,
   needs no correct singular, and cannot silently disagree with itself. It
   over-generates (policys is not a word) which costs nothing - a nonsense
   variant simply never appears in a question. */
function expand(w) {
  const v = new Set([w]);
  if (w.length > 4 && w.endsWith('ies')) v.add(w.slice(0, -3) + 'y');
  if (w.length > 3 && w.endsWith('es')) v.add(w.slice(0, -2));
  if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) v.add(w.slice(0, -1));
  if (w.length > 2 && w.endsWith('y')) v.add(w.slice(0, -1) + 'ies');
  v.add(w + 's');
  v.add(w + 'es');
  return v;
}
const overlaps = (a, b) => { for (const x of a) if (b.has(x)) return true; return false; };

/* The one list here that is NOT derived from the registry, and it needs its
   reason stated because the design above argues against exactly this.
   navSynonyms is written as natural phrases - "human in the loop", "owned by
   me", "what to do next", "third party" - which is right for AppShell's
   substring search but puts ordinary English into every bag. Scored by word
   intersection those words decide matches: before this list, "what is the
   weather" matched Supervision on "the", and "tell me about ai" matched Your
   AI agents on "me".
   The uniqueness rule cannot catch them - a stop word that appears in one bag
   looks perfectly distinctive. This is grammar rather than product
   vocabulary, so unlike a curated stop list of OUR words it does not drift
   as the nav changes. */
const STOP = new Set(('a an and are as at be been by can could did do doe for from get got had has have how i if in '
  + 'into is it its let me mine my next of on or our ours out set should show that the their them then there these '
  + 'they this to up us use used using want was we were what when where which who why will with would you your')
  .split(' '));

export const words = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  .split(' ').filter(Boolean).filter((w) => !STOP.has(w));

/* Every variant of every word in a question, so a term can be tested against
   it with one set lookup per variant. */
const questionVariants = (q) => {
  const out = new Set();
  for (const w of words(q)) for (const v of expand(w)) out.add(v);
  return out;
};

/* A surface's terms: its label plus its nav synonyms. The GROUP is
   deliberately excluded - "Discover", "Assess" and "Govern" are ordinary
   verbs, and letting them match would route half of all questions to
   whichever surface happened to come first. */
function termsFor(s) {
  return new Set([...words(s.label), ...words(synonymsFor(s.id))]);
}

/* Which terms belong to exactly one surface, across the WHOLE registry - not
   merely the surfaces this role can see. Computing it from the visible subset
   would make a word distinctive for a restricted role and ambiguous for an
   admin, so the same question would route differently for two people. The
   index is global; the matching is scoped. */
const TERMS = new Map(SURFACES.map((s) => [s.id, termsFor(s)]));

/* term -> its variant set, computed once. */
const VARIANTS = new Map();
for (const set of TERMS.values()) for (const t of set) if (!VARIANTS.has(t)) VARIANTS.set(t, expand(t));

/* Which terms belong to exactly one surface, by VARIANT overlap rather than
   string equality - otherwise "policy" and "policies" would count as two
   different distinctive terms owned by two different surfaces. */
const UNIQUE = (() => {
  const owners = new Map();                 // variant -> Set(surface id)
  for (const [id, set] of TERMS) {
    for (const t of set) {
      for (const v of VARIANTS.get(t)) {
        if (!owners.has(v)) owners.set(v, new Set());
        owners.get(v).add(id);
      }
    }
  }
  const u = new Map();
  for (const [id, set] of TERMS) {
    for (const t of set) {
      const ids = new Set();
      for (const v of VARIANTS.get(t)) for (const o of (owners.get(v) || [])) ids.add(o);
      if (ids.size === 1) u.set(t, id);
    }
  }
  return u;
})();

/**
 * @param question  what the user typed
 * @param visible   the surfaces this role may open - NOT the whole registry
 * @returns {surface, hit[], score} | {ambiguous:true, between:[...]} | null
 */
export function matchSurface(question, visible) {
  const q = questionVariants(question);
  if (!q.size || !Array.isArray(visible) || !visible.length) return null;

  /* Contiguous runs of the RAW question, stop words and all. The stop list
     is necessary for single-term scoring but it shreds the multi-word
     synonyms that are deliberately made of common words - audit's bag
     literally contains "who did what", and stripping those three left the
     question matching nothing. A run that appears verbatim in a surface's
     text is a strong, specific signal, so it is scored heavily. This is the
     same substring match AppShell's own nav search performs. */
  const raw = String(question).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);
  const runs = [];
  for (let n = Math.min(5, raw.length); n >= 2; n -= 1) {
    for (let i = 0; i + n <= raw.length; i += 1) runs.push(raw.slice(i, i + n).join(' '));
  }

  const hits = [];
  for (const s of visible) {
    const terms = TERMS.get(s.id) || termsFor(s);
    const hit = [...terms].filter((t) => overlaps(VARIANTS.get(t) || expand(t), q));
    const hay = (s.label + ' ' + synonymsFor(s.id)).toLowerCase();
    const phrase = runs.filter((r) => hay.includes(r));
    const best = phrase.length ? phrase[0] : null;

    /* ADMISSIBILITY FIRST, SCORE SECOND. An earlier version let any shared
       term in if only a few surfaces claimed it, on the theory that naming
       two pages beats silence. It produced "where do I block a tool" ->
       Inventory, MCP Gateway, Network Block List, with Policies - the right
       answer - cut by the cap because ties were broken alphabetically. Noise
       that crowds out the answer is worse than nothing.
       So a surface qualifies on evidence, not on a tunable threshold:
         - a verbatim phrase from the question appears in its vocabulary, or
         - two or more of its terms appear, or
         - one term appears and the registry says that term is its alone. */
    const admissible = best || hit.length >= 2 || (hit.length === 1 && UNIQUE.get(hit[0]) === s.id);
    if (!admissible) continue;

    /* A matched phrase outranks any number of scattered terms, which is why
       it is weighted an order of magnitude higher rather than by a few
       points: "shadow ai" appearing verbatim in AI Inventory's vocabulary is
       a different kind of evidence from three common words landing in AI
       Discovery's. */
    const score = (best ? best.split(' ').length * 10 : 0) + hit.length;
    hits.push({ surface: s, hit, phrase: best, score });
  }
  if (!hits.length) return null;

  /* Top plus anything within one point, not the single winner. Two surfaces
     can both be right: "how do I find shadow AI" genuinely spans AI
     Discovery, which is the page for finding candidates, and AI Inventory,
     which holds the Shadow AI status someone assigned. The product draws that
     line deliberately (ConsoleApp.jsx:298). Offering both is the honest
     answer and teaches the distinction; picking one silently does not.
     Capped at three so the reply stays a suggestion rather than a menu. */
  /* Alternatives only on an EXACT tie. A near-miss window sounded generous
     and was not: with phrase hits scoring an order of magnitude higher than
     term hits, "within one point" is arithmetically meaningless, and it
     attached a plausible-looking wrong page to answers that were already
     right. A genuine tie is genuine ambiguity and is worth showing; anything
     else has a winner, and saying so is the more useful answer.
     Alphabetical ordering within a tie is arbitrary but stable - it is a
     display order, never a tie-break that decides the destination. */
  hits.sort((a, b) => b.score - a.score || a.surface.label.localeCompare(b.surface.label));
  const top = hits[0];
  const tied = hits.filter((h) => h.score === top.score);
  return { ...top, alternatives: tied.slice(1, 3).map((h) => h.surface) };
}

/* Build-time validation, and it has a real job: navSynonyms is keyed by nav
   item id, and synonymsFor() is only ever called with one, so a key that is
   NOT an id is dead vocabulary - words a customer might type that will never
   match anything, in a file whose entire purpose is to catch those words.
   Nothing checked this before, which is how such keys accumulate. */
export function validateNavSynonyms(all = SURFACES) {
  const ids = new Set(all.map((s) => s.id));
  const dead = Object.keys(NAV_SYNONYMS).filter((k) => !ids.has(k));
  const noTerms = all.filter((s) => !termsFor(s).size).map((s) => s.id);
  return { dead, noTerms };
}

/* ---------------------------------------------------------------------------
   THE AI FALLBACK (K854)

   Keyword matching will always have holes, and they are not exotic ones: the
   page called Policies could not be found by the word "policy". Vocabulary
   can be widened forever and will still miss the next phrasing.

   So when the deterministic pass finds nothing, the model picks - but only
   FROM THE CATALOGUE, and the answer is validated here before it is used.
   That keeps both properties the deterministic design was protecting:

   spec section 6 - the model never emits a selector and never names a
     destination the caller did not offer. It returns an id; anything that is
     not an exact id in the list we sent is discarded. The worst a bad or
     adversarial completion can do is make the Guide say nothing.

   spec section 10 - guidance still works with no model at all. This runs
     only after the deterministic pass misses, so an unavailable or
     unconfigured provider degrades to exactly the previous behaviour rather
     than to an error.

   ROLE SCOPE survives too, because the catalogue sent is the role-filtered
   list and validation is against that same list. The model cannot route
   someone to a page they may not open, even if it names one.
--------------------------------------------------------------------------- */

/** The catalogue sent as context. Ids come from the generated registry. */
export function surfaceCatalogue(visible) {
  return (visible || []).map((s) => s.id + ' | ' + s.label + ' | ' + s.group).join('\n');
}

/** Validate a model reply against the catalogue that was actually sent. */
export function pickSurface(answer, visible) {
  const raw = String(answer || '').trim();
  if (!raw) return null;
  /* Take the first id-shaped token only. A model that ignores the format and
     writes a sentence must not be able to smuggle a second id, and "NONE"
     must not match a surface whose id happens to appear later in prose. */
  const first = (raw.match(/[a-z0-9_-]+/i) || [''])[0].toLowerCase();
  if (!first || first === 'none') return null;
  return (visible || []).find((s) => s.id.toLowerCase() === first) || null;
}

/**
 * Deterministic first, model second. `ask` is injected rather than imported
 * so this module stays free of network concerns and stays unit-testable.
 * @param ask  ({question, context, mode}) => Promise<{answer, error}>
 */
export async function matchSurfaceSmart(question, visible, ask) {
  const direct = matchSurface(question, visible);
  if (direct) return { ...direct, via: 'vocabulary' };
  if (typeof ask !== 'function' || !visible || !visible.length) return null;
  let answer;
  try {
    const res = await ask({ question, context: surfaceCatalogue(visible), mode: 'route' });
    if (!res || res.error) return null;
    answer = res.answer;
  } catch { return null; }          // never let routing break the reply
  const surface = pickSurface(answer, visible);
  return surface ? { surface, hit: [], phrase: null, score: 0, alternatives: [], via: 'ai' } : null;
}
